import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useBoolean } from '@/components/ui/lib/use-boolean';

/**
 * How long a navigation has to run before the shell admits to it. Anything that
 * resolves from the router cache lands well inside this window, so an instant
 * page swap never flashes an indicator — only a real wait does.
 */
const SHOW_AFTER_MS = 150;

/**
 * True while the router is resolving a navigation — the route's lazy chunk, its
 * `beforeLoad` guard, or the prefetch its `loader` awaits — and only once that
 * wait has outlasted `showAfterMs`.
 *
 * The router keeps the outgoing page mounted for the whole of that wait, so
 * without this the app looks frozen between a nav click and the next paint.
 */
export const useRoutePending = (showAfterMs: number = SHOW_AFTER_MS): boolean => {
  const isNavigating = useRouterState({
    select: (state) => state.isLoading || state.status === 'pending',
  });
  const [isPending, setIsPending] = useBoolean(false);

  useEffect(() => {
    if (!isNavigating) {
      setIsPending.off();
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsPending.on();
    }, showAfterMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isNavigating, showAfterMs, setIsPending]);

  return isPending;
};
